import type {ButtonHTMLAttributes, ReactNode} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {
    getButtonBackground,
    getButtonColor,
    getButtonFontSize,
    getButtonHeight,
    getButtonHoverBackground,
    getButtonIconSize,
    getButtonPadding
} from './utils';

import type {WithChildren} from 'types/global';

/** The available visual variants for the {@link Button} component. */
export type ButtonVariant = 'accent' | 'ghost' | 'primary' | 'primaryGradient';

/** The available size variants for the {@link Button} component. */
export type ButtonSize = 'large' | 'medium' | 'small';

/**
 * The props for the `Button` component.
 * Configures the visual variant and HTML button attributes.
 */
interface ComponentProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    /**
     * An optional icon element rendered before the button label.
     * Typically an SVG component or icon from the project's icon set.
     */
    icon?: ReactNode;
    /**
     * The size of the button.
     * - `'large'` — 4.8rem height, 1.4rem font (default Figma button).
     * - `'medium'` — 4.0rem height, 1.3rem font.
     * - `'small'` — 3.6rem height, 1.2rem font.
     *
     * @default 'large'
     */
    size?: ButtonSize;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'Button'
     */
    testId?: string;
    /**
     * The visual style variant of the button.
     * - `'primary'` — Solid forest green background, white text.
     * - `'primaryGradient'` — 135° gradient from primaryDefault to primaryLight, white text.
     * - `'accent'` — Warm amber background, dark accent text.
     * - `'ghost'` — Transparent with primaryDefault border, primaryDefault text.
     *
     * @default 'primary'
     */
    variant?: ButtonVariant;
}

/**
 * A pill-shaped button component matching the little-walks Figma design system.
 * Supports four visual variants (primary, primaryGradient, accent, ghost) and
 * a native disabled state that overrides to a muted appearance.
 * Renders as a native `<button>` element and forwards all standard button HTML attributes.
 *
 * @param props          The component props.
 * @param props.children The button label content.
 * @param props.icon     An optional icon element rendered before the label.
 * @param props.size     The size of the button. Defaults to `'large'`.
 * @param props.testId   An optional test ID used for automation selectors. Defaults to `'Button'`.
 * @param props.variant  The visual style variant. Defaults to `'primary'`.
 * @returns A JSX element rendering the Button.
 *
 * @example
 * ```tsx
 * <Button variant="primary" onClick={handleClick}>Tour starten</Button>
 * <Button variant="ghost" size="small">Alle anzeigen</Button>
 * <Button variant="accent" icon={<MapIcon />}>Karte laden</Button>
 * <Button disabled>Nicht verfügbar</Button>
 * ```
 */
const Button = ({
    children,
    icon,
    size = 'large',
    testId = 'Button',
    variant = 'primary',
    ...rest
}: WithChildren<ComponentProps>) => (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <StyledButton $size={size} $variant={variant} data-cy={testId} {...rest}>
        {icon && <IconWrapper $size={size} data-cy={`${testId}-icon`}>{icon}</IconWrapper>}
        {children}
    </StyledButton>
);

Button.displayName = 'Button';

export {Button};

// ── Styled ──────────────────────────────────────────────────

interface StyledButtonProps {
    /** The transient size prop for styling. */
    $size: ButtonSize;
    /** The transient variant prop for styling. */
    $variant: ButtonVariant;
}

const StyledButton = styled.button<StyledButtonProps>`
    align-items: center;
    background: ${getButtonBackground};
    border: ${({$variant, theme}) => (
        $variant === 'ghost'
            ? `1.5px solid ${theme.colors.primaryDefault}`
            : 'none'
    )};
    border-radius: 9999px;
    color: ${getButtonColor};
    cursor: pointer;
    display: inline-flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: ${getButtonFontSize};
    font-weight: 600;
    gap: ${spacing(2)};
    height: ${getButtonHeight};
    justify-content: center;
    line-height: 1;
    margin: 0;
    padding: ${getButtonPadding};
    text-decoration: none;
    transition: background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: ${getButtonHoverBackground};
    }

    &:active:not(:disabled) {
        opacity: 0.85;
    }

    &:disabled {
        background: ${({theme}) => theme.colors.inkDisabled};
        border: none;
        color: ${({theme}) => theme.colors.white};
        cursor: not-allowed;
    }
`;

interface IconWrapperProps {
    /** The transient size prop to scale the icon container. */
    $size: ButtonSize;
}

const IconWrapper = styled.span<IconWrapperProps>`
    align-items: center;
    display: inline-flex;
    flex-shrink: 0;
    height: ${getButtonIconSize};
    justify-content: center;
    width: ${getButtonIconSize};

    & > svg {
        height: 100%;
        width: 100%;
    }
`;